/**
 * Briques partagees de l'interface.
 *
 * Tout panneau modal du jeu herite de `Panneau`: meme fond assombri, meme
 * en-tete, meme fermeture (Echap, clic sur le fond, bouton), meme
 * journalisation. Les aides DOM evitent de repeter createElement partout.
 */
import '../styles/ui.css'
import type { Cout, Inventaire, ItemId } from '../../shared/types/items.ts'
import { objet } from '../../shared/data/objets.ts'
import { manquant, quantite } from '../../shared/sim/inventaire.ts'
import { audio } from '../audio/audio.ts'
import { journal } from '../log/journal.ts'
import { depiler, empiler, type Couche } from './retour.ts'

/** Etiquettes de journal autorisees pour l'interface. */
export type TagUi =
  | 'creation'
  | 'menu'
  | 'construire'
  | 'cuisine'
  | 'feu'
  | 'reglages'
  | 'admin'
  | 'ecole'
  | 'musee'
  | 'bibliotheque'
  | 'ascenseur'

// ------------------------------------------------------------------ aides DOM

/** Cree un element, avec sa classe et son texte. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  classe?: string,
  texte?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag)
  if (classe) e.className = classe
  if (texte !== undefined) e.textContent = texte
  return e
}

/**
 * Bouton pret a l'emploi: classe `.bouton` de base.css, son de clic et
 * declenchement sur `click` (donc aussi au doigt et au clavier).
 */
export function bouton(texte: string, onClic: () => void, classes?: string): HTMLButtonElement {
  const b = el('button', classes ? `bouton ${classes}` : 'bouton', texte)
  b.type = 'button'
  b.addEventListener('click', () => {
    clicSonore()
    onClic()
  })
  return b
}

/** Joue le clic d'interface si le moteur audio est deja pret. */
export function clicSonore(): void {
  if (audio.pret) audio.clic()
}

/** Joue le son d'action refusee si le moteur audio est deja pret. */
export function refusSonore(): void {
  if (audio.pret) audio.erreurInterface()
}

// --------------------------------------------------------------- couleurs

/** 0xRRGGBB -> '#rrggbb'. */
export function hex(couleur: number): string {
  const v = Math.max(0, Math.min(0xffffff, Math.floor(couleur)))
  return `#${v.toString(16).padStart(6, '0')}`
}

/** '#rrggbb' ou '#rgb' -> 0xRRGGBB. Rend 0 si la chaine est illisible. */
export function depuisHex(s: string): number {
  const t = s.trim().replace(/^#/, '')
  const complet = t.length === 3 ? `${t[0]}${t[0]}${t[1]}${t[1]}${t[2]}${t[2]}` : t
  const v = Number.parseInt(complet, 16)
  return Number.isFinite(v) ? Math.max(0, Math.min(0xffffff, v)) : 0
}

/** Vrai si la couleur est claire: sert a choisir la couleur du contour. */
function couleurClaire(couleur: number): boolean {
  const r = (couleur >> 16) & 255
  const g = (couleur >> 8) & 255
  const b = couleur & 255
  return 0.299 * r + 0.587 * g + 0.114 * b > 150
}

/** Pastille de couleur cliquable, taille adaptee au doigt. */
export function pastilleCouleur(couleur: number, actif: boolean, onClic: () => void): HTMLElement {
  const p = el('button', 'pastille')
  p.type = 'button'
  p.style.background = hex(couleur)
  p.title = hex(couleur)
  p.setAttribute('aria-label', `Couleur ${hex(couleur)}`)
  p.setAttribute('aria-pressed', actif ? 'true' : 'false')
  if (actif) p.classList.add('pastille--active')
  if (couleurClaire(couleur)) p.classList.add('pastille--claire')
  p.addEventListener('click', () => {
    clicSonore()
    onClic()
  })
  return p
}

/** Champ de couleur totalement libre, avec son etiquette. */
export function champCouleur(couleur: number, onChange: (c: number) => void): HTMLElement {
  const enveloppe = el('label', 'couleur-libre')
  const entree = el('input')
  entree.type = 'color'
  entree.value = hex(couleur)
  entree.className = 'couleur-libre__entree'
  entree.addEventListener('input', () => onChange(depuisHex(entree.value)))
  enveloppe.append(entree, el('span', 'couleur-libre__texte', 'Libre'))
  return enveloppe
}

// ------------------------------------------------------------------- couts

/** Entrees non vides d'un cout, dans l'ordre de declaration. */
function entreesCout(cout: Cout): { id: ItemId; n: number }[] {
  const sortie: { id: ItemId; n: number }[] = []
  for (const cle of Object.keys(cout) as ItemId[]) {
    const n = cout[cle]
    if (typeof n === 'number' && n > 0) sortie.push({ id: cle, n })
  }
  return sortie
}

/**
 * Ligne de cout: une puce par objet, en rouge avec la quantite manquante
 * quand l'inventaire ne suffit pas.
 */
export function afficherCout(cout: Cout, inv: Inventaire): HTMLElement {
  const ligne = el('div', 'cout')
  const trous = manquant(inv, cout)
  const entrees = entreesCout(cout)
  if (entrees.length === 0) {
    ligne.append(el('span', 'cout__gratuit', 'Gratuit'))
    return ligne
  }
  for (const { id, n } of entrees) {
    const def = objet(id)
    const trou = trous[id] ?? 0
    const puce = el('span', trou > 0 ? 'cout__objet cout__objet--manque' : 'cout__objet')
    puce.append(el('span', 'cout__icone', def.icone))
    puce.append(el('span', 'cout__nombre', `${quantite(inv, id)}/${n}`))
    if (trou > 0) puce.append(el('span', 'cout__trou', `il manque ${trou}`))
    puce.title = `${def.nom}: ${quantite(inv, id)} sur ${n}`
    ligne.append(puce)
  }
  return ligne
}

// ------------------------------------------------------------------ panneau

/** Duree de l'animation de fermeture, en ms. Doit suivre ui.css. */
const DUREE_FERMETURE = 190

/**
 * Base commune des panneaux modaux. La racine est un enfant direct du
 * conteneur d'interface, donc elle recoit les evenements de pointeur.
 */
export class Panneau {
  /** Fond assombri, enfant direct du parent. */
  readonly racine: HTMLDivElement
  /** Boite du panneau. */
  readonly boite: HTMLDivElement
  /** Zone defilante ou les sous-classes ecrivent leur contenu. */
  readonly corps: HTMLDivElement
  /** Bas du panneau, pour les boutons d'action. */
  readonly pied: HTMLDivElement
  /** Appele apres chaque fermeture, quelle qu'en soit la cause. */
  onFermer: (() => void) | null = null

  protected readonly tag: TagUi
  private readonly titreEl: HTMLElement
  private readonly iconeEl: HTMLElement
  private readonly fermeture: HTMLButtonElement
  private estOuvert = false
  /** Ce que la touche « retour » ferme quand ce panneau est devant. */
  private readonly couche: Couche
  private focusAvant: HTMLElement | null = null
  private minuterieCache: number | null = null

  private readonly surClavier = (e: KeyboardEvent): void => {
    if (!this.estOuvert) return
    if (e.key === 'Escape') {
      e.preventDefault()
      journal.input(this.tag, 'fermeture par Echap')
      this.fermer()
      return
    }
    if (e.key === 'Tab') this.piegerFocus(e)
  }

  private readonly surFond = (e: PointerEvent): void => {
    if (e.target !== this.racine) return
    journal.input(this.tag, 'fermeture par clic sur le fond')
    this.fermer()
  }

  constructor(parent: HTMLElement, tag: TagUi, titre: string, icone = '', classe = '') {
    this.tag = tag
    this.couche = { nom: tag, fermer: () => this.fermerParRetour() }
    this.racine = el('div', classe ? `panneau-fond ${classe}` : 'panneau-fond')
    this.racine.hidden = true
    this.racine.setAttribute('role', 'dialog')
    this.racine.setAttribute('aria-modal', 'true')

    this.boite = el('div', 'panneau')
    this.boite.tabIndex = -1

    const entete = el('div', 'panneau__entete')
    this.iconeEl = el('span', 'panneau__icone', icone)
    this.titreEl = el('h2', 'panneau__titre', titre)
    this.fermeture = el('button', 'panneau__fermer', '✕')
    this.fermeture.type = 'button'
    this.fermeture.title = 'Fermer'
    this.fermeture.setAttribute('aria-label', 'Fermer')
    this.fermeture.addEventListener('click', () => {
      clicSonore()
      journal.input(this.tag, 'fermeture par le bouton')
      this.fermer()
    })
    entete.append(this.iconeEl, this.titreEl, this.fermeture)

    this.corps = el('div', 'panneau__corps')
    this.pied = el('div', 'panneau__pied')

    this.boite.append(entete, this.corps, this.pied)
    this.racine.append(this.boite)
    this.racine.addEventListener('pointerdown', this.surFond)
    parent.append(this.racine)
  }

  get ouvert(): boolean {
    return this.estOuvert
  }

  /** Change le titre et le pictogramme affiches. */
  protected majEntete(titre: string, icone?: string): void {
    this.titreEl.textContent = titre
    if (icone !== undefined) this.iconeEl.textContent = icone
  }

  /** Vide la zone de contenu. */
  protected viderCorps(): void {
    this.corps.replaceChildren()
  }

  /** Vide la zone de boutons du bas. */
  protected viderPied(): void {
    this.pied.replaceChildren()
  }

  /** A appeler par la sous-classe a la fin de son `ouvrir(...)`. */
  protected afficher(detail?: string): void {
    if (this.minuterieCache !== null) {
      window.clearTimeout(this.minuterieCache)
      this.minuterieCache = null
    }
    if (!this.estOuvert) {
      this.estOuvert = true
      this.focusAvant = document.activeElement instanceof HTMLElement ? document.activeElement : null
      this.racine.hidden = false
      // Un souffle pour que la transition CSS demarre depuis l'etat ferme.
      requestAnimationFrame(() => this.racine.classList.add('panneau-fond--ouvert'))
      window.addEventListener('keydown', this.surClavier)
      empiler(this.couche)
      journal.input(this.tag, detail ? `panneau ouvert (${detail})` : 'panneau ouvert')
      this.boite.focus({ preventScroll: true })
    }
    this.corps.scrollTop = 0
  }

  /** Ferme le panneau. Les sous-classes surchargent `surFermeture` si besoin. */
  fermer(): void {
    if (!this.estOuvert) return
    depiler(this.couche)
    this.fermerSansHistorique()
  }

  /** « Retour » a deja retire l'entree d'historique: on ferme, simplement. */
  private fermerParRetour(): void {
    if (!this.estOuvert) return
    journal.input(this.tag, 'fermeture par la touche retour')
    this.fermerSansHistorique()
  }

  private fermerSansHistorique(): void {
    this.estOuvert = false
    this.racine.classList.remove('panneau-fond--ouvert')
    window.removeEventListener('keydown', this.surClavier)
    this.minuterieCache = window.setTimeout(() => {
      this.racine.hidden = true
      this.minuterieCache = null
    }, DUREE_FERMETURE)
    journal.input(this.tag, 'panneau ferme')
    this.surFermeture()
    if (this.focusAvant && this.focusAvant.isConnected) this.focusAvant.focus({ preventScroll: true })
    this.focusAvant = null
    this.onFermer?.()
  }

  /** Point d'extension: appele juste avant que la fermeture soit annoncee. */
  protected surFermeture(): void {
    /* rien par defaut */
  }

  /** Retire le panneau du document et tous ses ecouteurs. */
  detruire(): void {
    if (this.minuterieCache !== null) window.clearTimeout(this.minuterieCache)
    this.minuterieCache = null
    this.estOuvert = false
    window.removeEventListener('keydown', this.surClavier)
    this.racine.removeEventListener('pointerdown', this.surFond)
    this.racine.remove()
  }

  /** Garde la tabulation a l'interieur de la boite. */
  private piegerFocus(e: KeyboardEvent): void {
    const cibles = this.boite.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select, [tabindex]:not([tabindex="-1"])',
    )
    if (cibles.length === 0) return
    const premier = cibles[0]
    const dernier = cibles[cibles.length - 1]
    if (!premier || !dernier) return
    const actif = document.activeElement
    if (e.shiftKey && (actif === premier || actif === this.boite)) {
      e.preventDefault()
      dernier.focus()
    } else if (!e.shiftKey && actif === dernier) {
      e.preventDefault()
      premier.focus()
    }
  }
}

/** Titre de section a l'interieur d'un panneau. */
export function section(titre: string, icone?: string): HTMLDivElement {
  const s = el('div', 'section')
  const t = el('h3', 'section__titre')
  if (icone) t.append(el('span', 'section__icone', icone))
  t.append(el('span', 'section__texte', titre))
  s.append(t)
  return s
}

/** Grille de cartes, utilisee par la construction, l'inventaire et la cuisine. */
export function grille(classe = ''): HTMLDivElement {
  return el('div', classe ? `grille ${classe}` : 'grille')
}
